import { Color, Spacing } from '@persona-id/design-tokens';
import { MoreIcon } from '@persona-id/design-tokens/icons';
import { DateTime } from 'luxon';
import { useMemo, useState } from 'react';
import { connect, ConnectedProps } from 'react-redux';
import { Link } from 'react-router-dom-v5-compat';
import styled from 'styled-components';

import FeatureFlagged from 'dashboard/components/FeatureFlagged';
import NavBack from 'dashboard/components/NavBack';
import Pagination from 'dashboard/components/Pagination';
import Banner from 'dashboard/components-lib/Banner';
import { Backdrop, FullScreenHeaderContainer } from 'dashboard/layouts/ShowViewLayoutV2';
import { MessageType } from 'dashboard/lib/interfaces/Message';
import { SortParams } from 'dashboard/lib/interfaces/SortParams';
import { ReportRunMatchStatus } from 'dashboard/models/report-run';
import { ReportTemplateCustomList } from 'dashboard/models/report-template';
import {
  CustomListFileType,
  ReportTemplateVersionCustomList,
} from 'dashboard/models/report-template-version';
import { showMessage } from 'dashboard/redux/actions/messages';
import {
  setCurrentPage,
  setFilters,
  setPageSize,
  setSortParams,
} from 'dashboard/redux/actions/report-runs';
import { clearReportTemplateVersion } from 'dashboard/redux/actions/report-template-version';
import { RootState } from 'dashboard/redux/store';
import {
  clearReportRunsCustomList,
  loadReportRunsCustomList,
} from 'dashboard/redux/thunk-actions/report-runs';
import { loadReportTemplateVersion } from 'dashboard/redux/thunk-actions/report-template-version';
import { CustomListRunButton } from 'dashboard/views/DashboardReportTemplateVersionsShowView/CustomListRunButton';
import { default as WarningBanner } from 'design-system/components/Banner';
import Button from 'design-system/components/Button';
import Card from 'design-system/components/Card';
import DropdownMenu from 'design-system/components/DropdownMenu';
import Select, { Option } from 'design-system/components/Select';
import Spacer from 'design-system/components/Spacer';
import { HStack } from 'design-system/components/Stack';
import Sticker from 'design-system/components/Sticker';
import TabBar, { Tab } from 'design-system/components/TabBar';
import Table, {
  TableBody,
  TableCell,
  TableHead,
  TableHeaderCell,
  TableRow,
} from 'design-system/components/Table';
import { Heading, Paragraph } from 'design-system/components/Text';
import { HelperTooltip } from 'design-system/components/Tooltip';
import { paragraphMixins } from 'design-system/mixins/text';
import { DATE_AND_TIME_FORMAT } from 'lib/constants/luxonFormats';
import ApiResponseError from 'lib/errors/ApiResponseError';
import useApiLoader from 'lib/hooks/useApiLoader';
import useOnMountAndUnmount from 'lib/hooks/useOnMountAndUnmount';
import useTable from 'lib/hooks/useTable';
import { isPermissionsError, permissionsErrorMessage } from 'lib/permissions-errors';

enum CustomListSortParams {
  Result = 'result',
  CreatedAt = 'created_at',
}

type ReportTemplateCustomListOverviewPageProps = PropsFromRedux & {
  latestPublishedVersion: ReportTemplateVersionCustomList;
  reportTemplate: ReportTemplateCustomList;
  reportTemplateVersion: ReportTemplateVersionCustomList;
};

const ReportTemplateCustomListOverviewPage = (props: ReportTemplateCustomListOverviewPageProps) => {
  useOnMountAndUnmount(() => {
    props.clearReportTemplateVersion();
  });

  const { reportTemplate, reportTemplateVersion, latestPublishedVersion, reportRuns, sortParams } =
    props;

  const currentVersion = reportTemplateVersion ?? latestPublishedVersion;
  const [selectedVersionId, setSelectedVersionId] = useState(latestPublishedVersion.id);

  // Required to abort requests while props are changing quickly
  const abortController = useMemo(
    () => new AbortController(),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      props.loadReportRunsCustomList,
      props.loadReportTemplateVersion,
      props.currentEnvironment,
      props.currentPage,
      props.pageSize,
      props.sortParams,
      props.filters,
      currentVersion,
    ],
  );

  const onError = (error: ApiResponseError) => {
    if (isPermissionsError(error)) {
      props.showMessage(permissionsErrorMessage(error), MessageType.Failure);
    } else {
      props.showMessage('Failed to load reports.', MessageType.Failure);
    }
  };

  const onNetworkError = () =>
    props.showMessage('We are experiencing network issues. Please try again.', MessageType.Failure);

  const { loading: loadingReportTemplateVersion } = useApiLoader({
    loader: () =>
      props
        .clearReportRunsCustomList(props.currentEnvironment)
        .then(() => props.loadReportTemplateVersion(selectedVersionId)),
    abortController: abortController,
    onError,
    onNetworkError,
    deps: [
      selectedVersionId,
      props.clearReportRunsCustomList,
      props.currentEnvironment,
      props.loadReportTemplateVersion,
    ],
  });

  const { loading: loadingReportRuns } = useApiLoader({
    loader: () =>
      props.loadReportRunsCustomList({
        currentEnvironment: props.currentEnvironment,
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        currentPage: props.currentPage,
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        pageSize: props.pageSize,
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        sortParams: props.sortParams,
        filters: {
          ...props.filters,
          reportTemplateVersionIds: [selectedVersionId],
        },
      }),
    deps: [
      props.loadReportRunsCustomList,
      props.currentEnvironment,
      props.currentPage,
      props.pageSize,
      props.filters,
      props.sortParams,
      selectedVersionId,
    ],
    onError,
    onNetworkError,
    abortController: abortController,
  });

  const isLoading = loadingReportRuns || loadingReportTemplateVersion;

  const { handlePageLinkClick, handlePageSizeClick, handleSortUpdate } = useTable({
    filters: props.filters,
    sortParams: props.sortParams,
    pageSize: props.pageSize,
    currentPage: props.currentPage,
    setFilters: props.setFilters,
    setCurrentPage: props.setCurrentPage,
    setPageSize: props.setPageSize,
    setSortParams: props.setSortParams,
  });

  const handleSortClick = (sortParams: SortParams, sortKey: CustomListSortParams) => {
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    let newSortParams: SortParams = { sort: undefined, shouldSortDescending: false }; // unset the state
    const currentDirection = getSortDirection(sortParams, sortKey);
    if (currentDirection === false) {
      newSortParams = {
        sort: sortKey,
        shouldSortDescending: false, // default to ascending if first click
      };
    } else if (currentDirection === 'asc') {
      newSortParams = {
        sort: sortKey,
        shouldSortDescending: true,
      };
    }
    handleSortUpdate(newSortParams);
  };

  const sortedVersions = props.reportTemplate.attributes['version-history'].sort((a, b) => {
    return DateTime.fromISO(b['created-at']).diff(DateTime.fromISO(a['created-at']), 'seconds')
      .seconds;
  });

  const isRunning =
    currentVersion?.id === latestPublishedVersion?.id &&
    currentVersion?.attributes['custom-list-file-name'] &&
    currentVersion?.attributes['run-status'] === 'running';

  const noFileUploaded =
    currentVersion?.id === latestPublishedVersion?.id &&
    !currentVersion?.attributes['custom-list-file-name'];

  const isManualRunPolicy = currentVersion.attributes['run-policy'] === 'manual';

  const getFileTypeDisplay = () => {
    switch (currentVersion.attributes['custom-list-file-type']) {
      case CustomListFileType.FincenPerson:
        return 'FinCEN 314(a) Person';
      case CustomListFileType.FincenBusiness:
        return 'FinCEN 314(a) Business';
      case CustomListFileType.CustomScreening:
        return 'Custom Screening';
      default:
        return '';
    }
  };

  return (
    <>
      {isRunning && (
        <Banner backgroundColor={Color.CoreYellow400}>Report runs currently in progress.</Banner>
      )}
      <FullScreenHeaderContainer>
        <HeaderTitle>
          <NavBack to='/dashboard/report-templates' />
          <span>{reportTemplate?.attributes.name}</span>
        </HeaderTitle>
        <HStack>
          {isManualRunPolicy && (
            <CustomListRunButton
              width='auto'
              variant='primary'
              reportTemplateVersion={currentVersion}
            />
          )}
          <Link
            to={`/dashboard/report-templates/${reportTemplate.id}/${latestPublishedVersion.id}`}
          >
            <Button width='auto' variant='basic'>
              Configuration
            </Button>
          </Link>

          <FeatureFlagged featureFlag='global:enable-platform-connection-dashboard'>
            <DropdownMenu
              trigger={
                <Button aria-label='More actions' type='button' variant='inverse' width='icon'>
                  <MoreIcon />
                </Button>
              }
            >
              <DropdownMenu.Item
                as={Link}
                to={`/dashboard/platform-connections/${latestPublishedVersion.id}`}
              >
                Connections
              </DropdownMenu.Item>
            </DropdownMenu>
          </FeatureFlagged>
        </HStack>
      </FullScreenHeaderContainer>
      <Backdrop>
        {noFileUploaded && (
          <>
            <WarningBanner variant='warning' aria-live='polite'>
              <BannerPadding>
                No file is currently uploaded. Edit Configuration to get started on running reports.
              </BannerPadding>
            </WarningBanner>
            <Spacer height={2} />
          </>
        )}
        <TabBar aria-label='Custom list overview tabs'>
          <Tab id='custom-list-report-template__overview' selected={true}>
            Overview
          </Tab>
        </TabBar>
        <Spacer height={2} />
        <Card>
          <Card.Body>
            <Card.Title>Custom List Report Runs</Card.Title>
            <MetricsSection>
              <MetricsBox noBorder={true}>
                <Paragraph>Version</Paragraph>
                <Select
                  id='custom-list-report-template__version-select'
                  value={selectedVersionId}
                  onValueChange={(value) => {
                    const foundVersion = sortedVersions.find((v) => v.id === value);
                    if (foundVersion) {
                      setSelectedVersionId(foundVersion.id);
                    }
                  }}
                >
                  {sortedVersions.map((version, idx) => (
                    <Option key={version.id} value={version.id}>
                      {version.name}
                      {idx === 0 ? ` (latest)` : ''} - {version.id}
                    </Option>
                  ))}
                </Select>
              </MetricsBox>
              <MetricsBox noBorder={true}>
                <Paragraph>Type</Paragraph>
                <span>{getFileTypeDisplay()}</span>
              </MetricsBox>
              <MetricsBox noBorder={true}>
                <Paragraph>Created at</Paragraph>
                <span>
                  {currentVersion.attributes['created-at']
                    ? DateTime.fromISO(currentVersion.attributes['created-at']).toFormat(
                        DATE_AND_TIME_FORMAT,
                      )
                    : '–'}
                </span>
              </MetricsBox>
            </MetricsSection>
            <Spacer height={1} />
            <MetricsSection>
              <MetricsBox>
                <TextWithIcon>
                  <Paragraph>{isRunning ? 'Total screened' : 'Total to be screened'}</Paragraph>
                  <HelperTooltip size='sm' side='right'>
                    <HelperText>
                      {isRunning
                        ? 'Total number of reports that are to be completed. Numbers in “In Progress” and “Completed” should add up to this number.'
                        : 'Total number of reports that will be re-run when a run is started'}
                    </HelperText>
                  </HelperTooltip>
                </TextWithIcon>
                <Heading size='md' color='primary' as='p'>
                  {currentVersion.attributes['total-count']}
                </Heading>
              </MetricsBox>
              <MetricsBox>
                <TextWithIcon>
                  <Paragraph>Completed</Paragraph>
                  <HelperTooltip size='sm' side='right'>
                    <HelperText>Number of report runs that have completed</HelperText>
                  </HelperTooltip>
                </TextWithIcon>
                <Heading size='md' color='primary' as='p'>
                  {currentVersion.attributes['completed-count']}
                </Heading>
              </MetricsBox>
              <MetricsBox>
                <TextWithIcon>
                  <Paragraph>In progress</Paragraph>
                  <HelperTooltip size='sm' side='right'>
                    <HelperText>
                      Number of report runs still being processed in current screening
                    </HelperText>
                  </HelperTooltip>
                </TextWithIcon>
                <Heading size='md' color='primary' as='p'>
                  {currentVersion.attributes['in-progress-count']}
                </Heading>
              </MetricsBox>
              <MetricsBox>
                <TextWithIcon>
                  <Paragraph>Matches</Paragraph>
                  <HelperTooltip size='sm' side='right'>
                    <HelperText>Number of matches from report runs that have completed</HelperText>
                  </HelperTooltip>
                </TextWithIcon>
                <Heading size='md' color='primary' as='p'>
                  {currentVersion.attributes['matched-count']}
                </Heading>
              </MetricsBox>
            </MetricsSection>
            <Spacer height={1} />
            <Table as='div'>
              <TableHead as='div'>
                <TableRow as='div'>
                  <TableHeaderCell>Primary input</TableHeaderCell>
                  <TableHeaderCell
                    id='report-template-custom-list__sort-result'
                    role='columnheader'
                    sortable={true}
                    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
                    sortDirection={getSortDirection(sortParams, CustomListSortParams.Result)}
                    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
                    onClick={() => handleSortClick(sortParams, CustomListSortParams.Result)}
                  >
                    Status
                  </TableHeaderCell>
                  <TableHeaderCell>Report ID</TableHeaderCell>
                  <TableHeaderCell
                    id='report-template-custom-list__sort-created-at'
                    sortable={true}
                    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
                    sortDirection={getSortDirection(sortParams, CustomListSortParams.CreatedAt)}
                    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
                    onClick={() => handleSortClick(sortParams, CustomListSortParams.CreatedAt)}
                  >
                    Completed at (UTC)
                  </TableHeaderCell>
                </TableRow>
              </TableHead>
              <TableBody as='div'>
                {reportRuns.map((run) => (
                  <TableRow
                    as={Link}
                    to={`/dashboard/reports/${run.relationships?.report.data.id}/runs/${run.id}`}
                    key={run.id}
                  >
                    <TableCell as='span'>
                      {run.attributes['primary-input'] ?? <Paragraph>N/A</Paragraph>}
                    </TableCell>
                    <TableCell as='span'>
                      <Sticker variant={run.attributes.result === 'matched' ? 'pink' : 'grey'}>
                        {getMatchLabel(
                          run.attributes.result,
                          run.attributes['hit-entries']?.length,
                        )}
                      </Sticker>
                    </TableCell>
                    <TableCell as='span'>{run.relationships?.report.data.id}</TableCell>
                    <TableCell as='span'>
                      {run.attributes['completed-at']
                        ? DateTime.fromISO(run.attributes['completed-at']).toFormat(
                            DATE_AND_TIME_FORMAT,
                          )
                        : '–'}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            {reportRuns.length <= 0 && (
              <>
                <Spacer height={2} />
                <EmptyState>{isLoading ? 'Loading...' : 'No report runs completed'}</EmptyState>
                <Spacer height={2} />
              </>
            )}
            {reportRuns.length > 0 && (
              <Pagination
                pageSize={props.pageSize}
                paginationParams={props.paginationParams}
                handlePageSizeClick={handlePageSizeClick}
                handlePageLinkClick={handlePageLinkClick}
              />
            )}
          </Card.Body>
        </Card>
      </Backdrop>
    </>
  );
};

const getSortDirection = (sortParams: SortParams, sortKey: string) => {
  if (sortParams.sort !== sortKey) {
    return false;
  } else if (sortParams.shouldSortDescending) {
    return 'desc';
  } else {
    return 'asc';
  }
};

const getMatchLabel = (matchStatus: ReportRunMatchStatus, numMatches = 0) => {
  switch (matchStatus) {
    case ReportRunMatchStatus.Matched:
      return `Matched ${numMatches > 1 ? `(${numMatches})` : ''}`;
    case ReportRunMatchStatus.Dismissed:
      return 'Dismissed';
    case ReportRunMatchStatus.NoMatch:
    default:
      return 'No match';
  }
};

const MetricsSection = styled('div')`
  display: flex;
  flex-direction: row;
  column-gap: ${Spacing[4]};
`;

const MetricsBox = styled.div<{
  noBorder?: boolean;
}>`
  padding: ${Spacing[4]};
  border-radius: ${({ theme }) => theme.card.borderRadius};
  width: calc(25% - ${Spacing[4]});
  border: ${({ noBorder, theme }) =>
    noBorder ? 'initial' : `1px solid ${theme.color.border.primary}`};
  ${paragraphMixins.md};
`;

const TextWithIcon = styled.span`
  align-items: center;
  display: flex;
  column-gap: ${Spacing[1]};
`;

const EmptyState = styled.div`
  text-align: center;
`;

const HeaderTitle = styled.div`
  display: flex;
  align-items: center;
`;

const HelperText = styled.span`
  max-width: 300px;
`;

const BannerPadding = styled.span`
  padding: ${Spacing[2]};
`;

const mapStateToProps = (state: RootState) => {
  return {
    currentEnvironment: state.currentEnvironment.currentEnvironment,
    reportRuns: state.reportRuns.reportRuns[state.currentEnvironment.currentEnvironment.id] || [],
    paginationParams: state.reportRuns.paginationParams,
    filters: state.reportRuns.filters,
    sortParams: state.reportRuns.sortParams,
    currentPage: state.reportRuns.currentPage,
    pageSize: state.reportRuns.pageSize,
  };
};

const mapDispatchToProps = {
  loadReportRunsCustomList,
  clearReportRunsCustomList,
  clearReportTemplateVersion,
  loadReportTemplateVersion,
  setFilters,
  setSortParams,
  setCurrentPage,
  setPageSize,
  showMessage,
};

const connector = connect(mapStateToProps, mapDispatchToProps);
type PropsFromRedux = ConnectedProps<typeof connector>;

export default connector(ReportTemplateCustomListOverviewPage);
